import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import PropTypes from "prop-types";
import { Button } from "reactstrap";
import { Tooltip } from "antd";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "Components/CommonComponents/DocumentViewer.css";
import "./index.css";
import { isIOSWebKit } from "utilFunctions/shared/fileUtils";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const options = {
  cMapUrl: "cmaps/",
  cMapPacked: true,
  standardFontDataUrl: "standard_fonts/",
};
const TARGET_RENDER_PIXEL_RATIO = 2;
const IOS_MAX_CANVAS_PIXELS = 8_000_000;
const DEFAULT_MAX_CANVAS_PIXELS = 16_000_000;

function PDFPageviewer(props) {
  const { pdfUrl, allPageInOne, pdfPassword, isLibraryView, parentComponent } =
    props;
  // const [isFullScreen, setIsFullScreen] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageloading, setPageloading] = useState(false);
  const [scale, setScale] = useState(1);
  const [password, setPassword] = useState(pdfPassword);
  const [pDFError, setPDFError] = useState(null);
  const viewerRootRef = useRef(null);
  const slideStageRef = useRef(null);
  const [slideStageSize, setSlideStageSize] = useState({
    width: 0,
    height: 0,
  });
  const [pdfPageSize, setPdfPageSize] = useState(null);
  const isIOS = useMemo(() => isIOSWebKit(), []);
  // Keep one-page slide navigation consistent across every PDF entry point.
  const isSlideMode = true;
  // Rendering every high-DPI canvas at once can exceed WebKit's memory limit.
  const shouldRenderAllPages = allPageInOne && !isSlideMode;
  const pageWidth = 1000;
  const fitScale = useMemo(() => {
    if (!pdfPageSize || !slideStageSize.width || !slideStageSize.height) {
      return 1;
    }

    const availableWidth = Math.max(slideStageSize.width - 32, 1);
    const availableHeight = Math.max(slideStageSize.height - 32, 1);
    return Math.min(
      availableWidth / pdfPageSize.width,
      availableHeight / pdfPageSize.height
    );
  }, [pdfPageSize, slideStageSize]);
  const slideScale = fitScale * scale;
  const renderPixelRatio = useMemo(() => {
    if (!pdfPageSize) return 1;

    const renderedWidth = pdfPageSize.width * slideScale;
    const renderedHeight = pdfPageSize.height * slideScale;
    const renderedArea = Math.max(renderedWidth * renderedHeight, 1);
    const maxCanvasPixels = isIOS
      ? IOS_MAX_CANVAS_PIXELS
      : DEFAULT_MAX_CANVAS_PIXELS;
    const safePixelRatio = Math.sqrt(maxCanvasPixels / renderedArea);

    return Math.max(1, Math.min(TARGET_RENDER_PIXEL_RATIO, safePixelRatio));
  }, [isIOS, pdfPageSize, slideScale]);
  const zoomPercentage = Math.round(scale * 100);
  const onDocumentLoadSuccess = useCallback(
    ({ numPages }) => {
      setPDFError(null);
      setNumPages(numPages);
      setPageNumber(1);
    },
    [pdfUrl]
  );
  useEffect(() => {
    setPassword(pdfPassword);
  }, [pdfPassword]);
  useEffect(() => {
    setScale(1);
    setPageNumber(1);
    setPdfPageSize(null);
    setPageloading(true);
  }, [pdfUrl]);
  useEffect(() => {
    const modal = viewerRootRef.current?.closest(".modal-dialog, .ant-modal");
    if (!modal) return undefined;

    modal.classList.add("document-viewer-fullscreen-modal");
    return () => modal.classList.remove("document-viewer-fullscreen-modal");
  }, []);
  useEffect(() => {
    if (!isSlideMode || !slideStageRef.current) return undefined;

    const stage = slideStageRef.current;
    const updateStageSize = () => {
      const nextSize = {
        width: stage.clientWidth,
        height: stage.clientHeight,
      };
      setSlideStageSize((currentSize) => {
        if (
          currentSize.width === nextSize.width &&
          (isIOS || currentSize.height === nextSize.height)
        ) {
          return currentSize;
        }
        return nextSize;
      });
    };

    updateStageSize();
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateStageSize);
    resizeObserver?.observe(stage);
    window.addEventListener("orientationchange", updateStageSize);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("orientationchange", updateStageSize);
    };
  }, [isIOS, isSlideMode]);
  // const toggleFullScreen = () => {
  //   if (!isFullScreen) {
  //     console.log("fullscreen", isFullScreen, document.documentElement);
  //     if (document.documentElement.requestFullscreen) {
  //       document.documentElement.requestFullscreen();
  //     } else if (document.documentElement.mozRequestFullScreen) {
  //       document.documentElement.mozRequestFullScreen();
  //     } else if (document.documentElement.webkitRequestFullscreen) {
  //       document.documentElement.webkitRequestFullscreen();
  //     } else if (document.documentElement.msRequestFullscreen) {
  //       document.documentElement.msRequestFullscreen();
  //     }
  //   } else {
  //     console.log("fullscreen", isFullScreen);
  //     if (document.exitFullscreen) {
  //       document.exitFullscreen();
  //     } else if (document.mozCancelFullScreen) {
  //       document.mozCancelFullScreen();
  //     } else if (document.webkitExitFullscreen) {
  //       document.webkitExitFullscreen();
  //     } else if (document.msExitFullscreen) {
  //       document.msExitFullscreen();
  //     }
  //   }
  //   setIsFullScreen(!isFullScreen);
  // };
  const changePage = (offset) => {
    setPageloading(true);
    setPdfPageSize(null);
    setScale(1);
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  };

  const previousPage = () => {
    changePage(-1);
  };

  const nextPage = () => {
    changePage(1);
  };
  const file = useMemo(
    () => ({
      url: pdfUrl,
    }),
    [pdfUrl]
  );
  const handleScale = (type) => {
    if (type == "reset") {
      setScale(1);
    } else if (type == "plus") {
      setScale((currentScale) => Math.min(2, Number(currentScale) + 0.1));
    } else {
      setScale((currentScale) => Math.max(0.5, Number(currentScale) - 0.1));
    }
  };
  const onPageLoadSuccess = useCallback((page) => {
    const viewport = page.getViewport({ scale: 1 });
    setPdfPageSize((currentSize) => {
      if (
        currentSize?.width === viewport.width &&
        currentSize?.height === viewport.height
      ) {
        return currentSize;
      }
      return { width: viewport.width, height: viewport.height };
    });
  }, []);

  if (shouldRenderAllPages) {
    return (
      <div ref={viewerRootRef} className="pdf-viewer-root">
        <Document
          className={`pdf-document-viewer ${!pageloading ? "pdf-loaded" : ""}`}
          file={file}
          onPassword={(callback) => callback(password)}
          onContextMenu={(event) => event.preventDefault()}
          loading={<div className="pdfLoadingContainer" />}
          externalLinkRel="_blank"
          externalLinkTarget="_blank"
          error={
            <div className="pdfErrorContainer">Failed to load PDF file.</div>
          }
          onLoadError={setPDFError}
          options={options}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {Array.from(new Array(numPages), (element, index) => (
            <div
              key={`page_container_${index + 1}`}
              className="pdf-page-container"
            >
              <Page
                scale={Math.min(2, scale * 1.1)}
                width={pageWidth}
                renderAnnotationLayer={true}
                renderTextLayer={true}
                loading={<div className="pdfLoadingContainer" />}
                onRenderSuccess={() => setPageloading(false)}
                externalLinkTarget="_blank"
                externalLinkRel="_blank"
                pageNumber={index + 1}
              />
              <div className="pdf-page-number-indicator">
                Page {index + 1} of {numPages}
              </div>
            </div>
          ))}
        </Document>
        <div
          className={
            isLibraryView
              ? parentComponent === "AssetViewerModal"
                ? "pdf-button-container-library-modal"
                : parentComponent === "PlannerViewHome"
                ? "pdf-button-container-library-planner"
                : "pdf-button-container-library"
              : "pdf-button-container"
          }
        >
          {pDFError == null && (
            <div className="pdf-zoom-controls">
              <Tooltip title="Zoom Out">
                <Button
                  className="pdf-zoom-button"
                  onClick={() => handleScale("minus")}
                >
                  −
                </Button>
              </Tooltip>
              <Tooltip title="Reset to Original Size">
                <Button
                  className="pdf-zoom-button"
                  onClick={() => handleScale("reset")}
                >
                  ⟲
                </Button>
              </Tooltip>
              <Tooltip title="Zoom In">
                <Button
                  className="pdf-zoom-button"
                  onClick={() => handleScale("plus")}
                >
                  +
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={viewerRootRef} className="pdf-viewer-root pdf-slide-viewer">
      <div ref={slideStageRef} className="pdf-slide-stage">
        <div className="pdf-slide-scroll-area">
          <Document
            className="pdf-slide-document"
            file={file}
            onPassword={(callback) => callback(password)}
            onContextMenu={(event) => event.preventDefault()}
            loading={<div className="pdfLoadingContainer" />}
            externalLinkRel="_blank"
            externalLinkTarget="_blank"
            error={
              <div className="pdfErrorContainer">Failed to load PDF file.</div>
            }
            onLoadError={setPDFError}
            options={options}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            <Page
              key={`${pdfUrl}_${pageNumber}`}
              scale={slideScale}
              devicePixelRatio={renderPixelRatio}
              canvasBackground="white"
              renderAnnotationLayer={false}
              renderTextLayer={false}
              loading={<div className="pdfLoadingContainer" />}
              onLoadSuccess={onPageLoadSuccess}
              onRenderSuccess={() => setPageloading(false)}
              pageNumber={pageNumber}
              className="pdf-slide-page"
              style={{
                visibility:
                  pdfPageSize && slideStageSize.width ? "visible" : "hidden",
              }}
            />
          </Document>
        </div>

        {pDFError == null && (
          <>
            <button
              type="button"
              className="pdf-slide-arrow pdf-slide-arrow-previous"
              disabled={pageNumber <= 1}
              onClick={previousPage}
              aria-label="Previous slide"
            >
              ‹
            </button>
            <button
              type="button"
              className="pdf-slide-arrow pdf-slide-arrow-next"
              disabled={!numPages || pageNumber >= numPages}
              onClick={nextPage}
              aria-label="Next slide"
            >
              ›
            </button>
            <div className="pdf-slide-page-count" aria-live="polite">
              {pageNumber} / {numPages || "--"}
            </div>
          </>
        )}
      </div>

      {pDFError == null && (
        <div className="pdf-slide-toolbar">
          <button
            type="button"
            className="pdf-slide-zoom-button"
            disabled={scale <= 0.5}
            onClick={() => handleScale("minus")}
            aria-label="Zoom out"
          >
            −
          </button>
          <span className="pdf-slide-zoom-value">{zoomPercentage}%</span>
          <button
            type="button"
            className="pdf-slide-zoom-button"
            disabled={scale >= 2}
            onClick={() => handleScale("plus")}
            aria-label="Zoom in"
          >
            +
          </button>
          <span className="pdf-slide-toolbar-divider" aria-hidden="true" />
          <button
            type="button"
            className="pdf-slide-reset-button"
            onClick={() => handleScale("reset")}
            aria-label="Reset zoom"
          >
            ↻
          </button>
        </div>
      )}
    </div>
  );
}

PDFPageviewer.propTypes = {
  pdfUrl: PropTypes.string,
  allPageInOne: PropTypes.bool,
  pdfPassword: PropTypes.string,
  isFullScreen: PropTypes.bool,
  setIsFullScreen: PropTypes.func,
  isLibraryView: PropTypes.bool,
  source: PropTypes.string,
  parentComponent: PropTypes.string,
};
export default PDFPageviewer;
